import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map as MapIcon, RefreshCw, Plus, MapPin, Loader2, Satellite, Upload, Building2, Trash2, Layers, HelpCircle } from 'lucide-react';
import useMapStore from '../stores/mapStore';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import mapTutorial from '../components/tutorials/configs/mapTutorial';

export default function MapPage() {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(mapTutorial);
  const { floorPlans, loading, error, fetchFloorPlans, fetchSettings, deleteSurvey, clearError } = useMapStore();
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    fetchFloorPlans();
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchFloorPlans();
    setRefreshing(false);
  }, []);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete map "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    await deleteSurvey(id);
    setDeleting(null);
  };

  // Group surveys by building
  const grouped = {};
  (floorPlans || []).forEach(fp => {
    const key = fp.buildingName || 'Ungrouped';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(fp);
  });
  // Sort each group by floor number
  Object.values(grouped).forEach(arr => arr.sort((a, b) => a.floorNumber - b.floorNumber));

  const totalSurveys = (floorPlans || []).length;

  // ── No surveys: Welcome screen ──────────────────────────
  if (!loading && totalSurveys === 0) {
    return (
      <div className="md-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="page-header-with-actions">
          <div className="page-header-content">
            <h1 className="page-title"><MapIcon size={22} /> Site Map</h1>
            <p className="page-subtitle">Site maps and floor plans</p>
          </div>
        </div>

        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '20px',
          textAlign: 'center', padding: '40px',
        }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: 'rgba(0,212,170,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MapPin size={36} style={{ color: '#00d4aa' }} />
          </div>
          <h2 style={{ color: 'var(--text-primary, #e2e8f0)', margin: 0 }}>No Site Maps Yet</h2>
          <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: 0, maxWidth: '440px', fontSize: '14px', lineHeight: '1.6' }}>
            Create your first site map to start mapping devices.
            Use satellite imagery for exterior views or upload floor plans for interior layouts.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button className="btn btn-primary" onClick={() => navigate('/map/survey/new')} style={{ padding: '12px 24px', fontSize: '15px' }}>
              <Plus size={18} />
              Create First Map
            </button>
          </div>
          <div style={{ display: 'flex', gap: '24px', marginTop: '24px', color: 'var(--text-secondary, #94a3b8)', fontSize: '13px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Satellite size={16} style={{ color: '#00d4aa' }} />
              Satellite view for exteriors
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Upload size={16} style={{ color: '#0ea5e9' }} />
              Import PDF/image floor plans
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Layers size={16} style={{ color: '#a855f7' }} />
              Multiple buildings & floors
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Surveys exist: Grid view ────────────────────────────
  return (
    <div className="md-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CcmLicenseBanner />
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title"><MapIcon size={22} /> Site Map</h1>
          <p className="page-subtitle">Site maps and floor plans</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => navigate('/map/survey/new')}>
            <Plus size={16} />
            New Map
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(mapTutorial.module, mapTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
          <span className="camera-count">{totalSurveys} map{totalSurveys !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px 16px', margin: '0 0 8px 0',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px', color: '#ef4444', fontSize: '13px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{error}</span>
          <button onClick={clearError} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', gap: '8px' }}>
          <Loader2 size={20} className="spin" /> Loading maps...
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', paddingBottom: '16px' }}>
          {Object.entries(grouped).map(([building, surveys]) => (
            <div key={building} style={{ marginBottom: '24px' }}>
              {Object.keys(grouped).length > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '12px', color: 'var(--text-secondary, #94a3b8)', fontSize: '13px', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  <Building2 size={14} />
                  {building}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {surveys.map(survey => (
                  <SurveyCard
                    key={survey.id}
                    survey={survey}
                    onDelete={() => handleDelete(survey.id, survey.name)}
                    deleting={deleting === survey.id}
                    onClick={() => navigate(`/map/survey/${survey.id}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={mapTutorial.module}
          moduleName={mapTutorial.moduleName}
          steps={mapTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
}

function SurveyCard({ survey, onClick, onDelete, deleting }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-secondary, #1e293b)',
        border: '1px solid var(--border-color, #334155)',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00d4aa'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-color, #334155)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Thumbnail */}
      <div style={{ width: '100%', height: '160px', background: '#0f172a', position: 'relative', overflow: 'hidden' }}>
        {survey.imageUrl ? (
          <img src={survey.imageUrl} alt={survey.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-secondary, #94a3b8)' }}>
            <MapPin size={32} />
          </div>
        )}
        {/* Source type badge */}
        <span style={{
          position: 'absolute', top: '8px', left: '8px',
          background: survey.sourceType === 'satellite' ? 'rgba(0,212,170,0.9)' : 'rgba(14,165,233,0.9)',
          color: '#fff', fontSize: '11px', fontWeight: 600,
          padding: '3px 8px', borderRadius: '4px',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          {survey.sourceType === 'satellite' ? <Satellite size={12} /> : <Upload size={12} />}
          {survey.sourceType === 'satellite' ? 'Satellite' : 'Imported'}
        </span>
      </div>

      {/* Info */}
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600 }}>
              {survey.name}
            </h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--text-secondary, #94a3b8)' }}>
              {survey.buildingName && <span>{survey.buildingName}</span>}
              {survey.floorNumber != null && survey.floorNumber !== 0 && survey.floorNumber !== "0" && <span>Floor {survey.floorNumber}</span>}
              {survey.address && <span>{survey.address}</span>}
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            disabled={deleting}
            style={{
              background: 'none', border: 'none',
              color: 'var(--text-secondary, #94a3b8)',
              cursor: 'pointer', padding: '4px',
              borderRadius: '4px',
            }}
            title="Delete map"
          >
            {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
          </button>
        </div>
        {survey.description && (
          <p style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '12px', margin: '8px 0 0 0', lineHeight: '1.4' }}>
            {survey.description}
          </p>
        )}
      </div>
    </div>
  );
}
